import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, Loader2, Send, Copy, Code, EyeOff, Eye, Sparkles, Mail } from "lucide-react";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;

function Stars({ n, size = "w-4 h-4" }) {
  return <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((i) => (
    <Star key={i} className={`${size} ${i <= n ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />))}</div>;
}

export default function Reputation() {
  const { activeSubId } = useAuth();
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState({ google_url: "", request_message: "", min_public_rating: 4 });
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [reqForm, setReqForm] = useState({ tags: [], channel: "email", message: "" });

  const load = useCallback(() => {
    if (!activeSubId) return;
    setLoading(true);
    Promise.all([
      api.get(`/reputation/reviews?sub_account_id=${activeSubId}`),
      api.get(`/reputation/settings?sub_account_id=${activeSubId}`),
      api.get(`/contacts/tags?sub_account_id=${activeSubId}`),
    ]).then(([r, s, t]) => { setData(r.data); setSettings(s.data); setTags(t.data); setLoading(false); });
  }, [activeSubId]);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try { await api.put(`/reputation/settings?sub_account_id=${activeSubId}`, settings); toast.success("Settings saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleTag = (t) => setReqForm((f) => ({ ...f, tags: f.tags.includes(t) ? f.tags.filter((x) => x !== t) : [...f.tags, t] }));

  const sendRequests = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/reputation/request?sub_account_id=${activeSubId}`, reqForm);
      toast.success(`Sent ${data.count} review request${data.count === 1 ? "" : "s"}`);
      setReqOpen(false); setReqForm({ tags: [], channel: "email", message: "" });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const updateReview = async (id, patch) => {
    await api.put(`/reputation/reviews/${id}`, patch);
    load();
  };

  const iframe = `<iframe src="${BASE}/widget/reviews/${activeSubId}" style="width:100%;max-width:640px;height:520px;border:0" title="Reviews"></iframe>`;
  const reviewLink = `${BASE}/r/${activeSubId}`;

  if (loading || !data) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const s = data.summary;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="reputation-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Reviews & Reputation</p>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mt-1">Reputation</h1>
        </div>
        <Dialog open={reqOpen} onOpenChange={setReqOpen}>
          <DialogTrigger asChild><Button className="gap-2 font-semibold" data-testid="request-reviews-button"><Send className="w-4 h-4" />Request reviews</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Send review requests</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5"><Label>Channel</Label>
                <Select value={reqForm.channel} onValueChange={(v) => setReqForm({ ...reqForm, channel: v })}>
                  <SelectTrigger data-testid="req-channel"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="email">Email</SelectItem><SelectItem value="telegram">Telegram</SelectItem></SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Send to tags <span className="text-muted-foreground font-normal">(none = all contacts)</span></Label>
                <div className="flex flex-wrap gap-1.5">
                  {tags.map((t) => (
                    <button key={t} onClick={() => toggleTag(t)} type="button"
                      className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${reqForm.tags.includes(t) ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}
                      data-testid={`req-tag-${t}`}>{t}</button>
                  ))}
                </div></div>
              <div className="space-y-1.5"><Label>Message <span className="text-muted-foreground font-normal">(optional — uses default)</span></Label>
                <Textarea value={reqForm.message} onChange={(e) => setReqForm({ ...reqForm, message: e.target.value })} rows={3} placeholder={settings.request_message} data-testid="req-message" /></div>
            </div>
            <DialogFooter><Button onClick={sendRequests} disabled={saving} className="gap-2" data-testid="send-requests-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Send requests</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        <Card className="p-6 bg-card border-border/70 flex items-center gap-4" data-testid="rep-average">
          <div className="text-4xl font-display font-bold">{s.average || "—"}</div>
          <div><Stars n={Math.round(s.average)} /><p className="text-xs text-muted-foreground mt-1">{s.public_count} public reviews</p></div>
        </Card>
        <Card className="p-6 bg-card border-border/70 sm:col-span-2" data-testid="rep-distribution">
          <div className="space-y-1.5">
            {[5, 4, 3, 2, 1].map((r) => {
              const count = s.distribution[r] || 0;
              const pct = s.public_count ? (count / s.public_count) * 100 : 0;
              return (
                <div key={r} className="flex items-center gap-3 text-xs">
                  <span className="w-3 text-muted-foreground">{r}</span><Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                  <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-amber-400" style={{ width: `${pct}%` }} /></div>
                  <span className="w-6 text-right text-muted-foreground">{count}</span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      <Tabs defaultValue="reviews">
        <TabsList>
          <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews ({data.reviews.length})</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-rep-settings">Settings</TabsTrigger>
          <TabsTrigger value="widget" data-testid="tab-widget">Widget</TabsTrigger>
        </TabsList>

        <TabsContent value="reviews" className="mt-6">
          <Card className="bg-card border-border/70 divide-y divide-border/60">
            {data.reviews.length === 0 && <div className="p-10 text-center text-muted-foreground" data-testid="reviews-empty">No reviews yet. Request some from happy clients.</div>}
            {data.reviews.map((r) => (
              <div key={r.id} className="flex flex-wrap items-start gap-3 px-5 py-4" data-testid={`review-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Stars n={r.rating} />
                    <span className="font-medium text-sm">{r.name}</span>
                    {!r.is_public && <Badge variant="secondary" className="text-[10px] text-rose-400">Private feedback</Badge>}
                    {r.featured && <Badge variant="secondary" className="text-[10px] text-amber-400 gap-0.5"><Sparkles className="w-2.5 h-2.5" />Featured</Badge>}
                    {r.status === "hidden" && <Badge variant="secondary" className="text-[10px]">Hidden</Badge>}
                  </div>
                  {r.comment && <p className="text-sm text-muted-foreground mt-1.5">{r.comment}</p>}
                  <p className="text-[11px] text-muted-foreground/70 mt-1">{new Date(r.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex gap-1">
                  {r.is_public && (
                    <button onClick={() => updateReview(r.id, { featured: !r.featured })} title="Feature" className="text-muted-foreground hover:text-amber-400" data-testid={`feature-${r.id}`}><Sparkles className="w-4 h-4" /></button>
                  )}
                  {r.status === "approved" ? (
                    <button onClick={() => updateReview(r.id, { status: "hidden" })} title="Hide" className="text-muted-foreground hover:text-foreground" data-testid={`hide-${r.id}`}><EyeOff className="w-4 h-4" /></button>
                  ) : r.status === "hidden" ? (
                    <button onClick={() => updateReview(r.id, { status: "approved" })} title="Show" className="text-muted-foreground hover:text-emerald-400" data-testid={`show-${r.id}`}><Eye className="w-4 h-4" /></button>
                  ) : null}
                </div>
              </div>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <Card className="p-6 bg-card border-border/70 max-w-xl space-y-5">
            <div className="space-y-1.5"><Label>Google / Facebook review URL</Label>
              <Input value={settings.google_url} onChange={(e) => setSettings({ ...settings, google_url: e.target.value })} placeholder="https://g.page/r/…/review" data-testid="google-url-input" />
              <p className="text-xs text-muted-foreground">Happy customers ({settings.min_public_rating}★+) are redirected here to post publicly.</p></div>
            <div className="space-y-1.5"><Label>Minimum public rating</Label>
              <Select value={String(settings.min_public_rating)} onValueChange={(v) => setSettings({ ...settings, min_public_rating: parseInt(v) })}>
                <SelectTrigger className="w-32" data-testid="min-rating-select"><SelectValue /></SelectTrigger>
                <SelectContent>{[3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}★ and up</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Below this, feedback is captured privately to protect your reputation.</p></div>
            <div className="space-y-1.5"><Label>Request message</Label>
              <Textarea value={settings.request_message} onChange={(e) => setSettings({ ...settings, request_message: e.target.value })} rows={3} data-testid="request-message-input" /></div>
            <Button onClick={saveSettings} disabled={saving} className="font-semibold" data-testid="save-rep-settings">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save settings"}</Button>
            <div className="pt-4 border-t border-border/60">
              <Label className="text-xs">Public review link</Label>
              <div className="flex items-center gap-2 mt-1.5">
                <Input readOnly value={reviewLink} className="font-mono text-xs" data-testid="review-link" />
                <Button variant="outline" size="sm" onClick={() => { navigator.clipboard?.writeText(reviewLink); toast.success("Link copied"); }} data-testid="copy-review-link"><Copy className="w-4 h-4" /></Button>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="widget" className="mt-6">
          <Card className="p-6 bg-card border-border/70 max-w-2xl space-y-4">
            <div className="flex items-center gap-2"><Code className="w-4 h-4 text-primary" /><h3 className="font-display font-semibold text-lg">Embeddable review widget</h3></div>
            <p className="text-sm text-muted-foreground">Paste this snippet on any website to show your approved reviews with live average rating.</p>
            <div className="relative">
              <pre className="rounded-lg bg-secondary/50 border border-border/60 p-4 text-xs font-mono overflow-x-auto" data-testid="widget-snippet">{iframe}</pre>
              <Button variant="outline" size="sm" className="absolute top-2 right-2 gap-1.5" onClick={() => { navigator.clipboard?.writeText(iframe); toast.success("Snippet copied"); }} data-testid="copy-widget-snippet"><Copy className="w-3.5 h-3.5" />Copy</Button>
            </div>
            <a href={`${BASE}/widget/reviews/${activeSubId}`} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline inline-flex items-center gap-1.5" data-testid="preview-widget"><Eye className="w-4 h-4" />Preview widget</a>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
